import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CountrySelector } from '@/features/countrySelector';
import type { TelephonyType, DirectionType } from '@intone/shared/types';

export interface NewCampaignData {
  name: string;
  telephony: NonNullable<TelephonyType>;
  direction: DirectionType;
  country: string | null;
}

type DialogNewCampaignProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: NewCampaignData) => void;
  existingNames: string[];
};

const CAMPAIGN_TYPE_OPTIONS = [
  { value: 'twilio/inbound', label: 'Phone Inbound', description: 'Receive calls on a dedicated phone number' },
  { value: 'asterisk/outbound', label: 'Phone Outbound', description: 'Use our dialer to make outbound calls' },
  { value: 'custom/inbound', label: 'Text Inbound', description: 'Receive and respond to text messages' },
] as const;

export function DialogNewCampaign({ isOpen, onClose, onConfirm, existingNames }: DialogNewCampaignProps) {
  const [name, setName] = useState('');
  const [telephonyValue, setTelephonyValue] = useState<string>('asterisk/outbound');
  const [country, setCountry] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isPhoneCampaign = telephonyValue !== 'custom/inbound';

  const handleConfirm = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Campaign name is required');
      return;
    }
    if (existingNames.some(n => n.toLowerCase() === trimmed.toLowerCase())) {
      setError('A campaign with this name already exists');
      return;
    }

    const [telephony, direction] = telephonyValue.split('/') as [NonNullable<TelephonyType>, DirectionType];
    onConfirm({ name: trimmed, telephony, direction, country: isPhoneCampaign ? country : null });

    setName('');
    setTelephonyValue('asterisk/outbound');
    setCountry(null);
    setError(null);
  };

  const handleClose = () => {
    setName('');
    setTelephonyValue('asterisk/outbound');
    setCountry(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Campaign</DialogTitle>
          <DialogDescription>Configure the basic settings. Campaign type cannot be changed later.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <label className="text-detail font-medium text-muted-foreground">Campaign Name</label>
            <Input
              value={name}
              onChange={(e) => { setName(e.target.value); setError(null); }}
              placeholder="e.g. Sales US Q1"
              className="h-8"
              autoFocus
            />
            {error && <p className="text-detail text-destructive">{error}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-detail font-medium text-muted-foreground">Campaign Type</label>
            <RadioGroup
              value={telephonyValue}
              onValueChange={setTelephonyValue}
              className="space-y-1"
            >
              {CAMPAIGN_TYPE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-start gap-2 rounded-md border p-2 cursor-pointer hover:bg-muted/50 has-[button[data-state=checked]]:border-primary has-[button[data-state=checked]]:bg-primary/5"
                >
                  <RadioGroupItem value={opt.value} className="mt-0.5" />
                  <div>
                    <div className="text-detail font-medium">{opt.label}</div>
                    <div className="text-detail text-muted-foreground">{opt.description}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          {isPhoneCampaign && (
            <div className="space-y-1">
              <label className="text-detail font-medium text-muted-foreground">Country</label>
              <CountrySelector
                value={country}
                onChange={setCountry}
                placeholder="Select country"
              />
              <p className="text-detail text-muted-foreground">
                Used for matching phone numbers to your campaign
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleConfirm}>Create Campaign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
